import type { BattleEvent } from './types';

interface ParseContext {
  p1Name: string;
  p2Name: string;
}

/**
 * Project a single Showdown protocol line to a BattleEvent we display, or
 * null for lines we ignore (every protocol verb we don't render passes
 * through without throwing).
 */
export function parseLine(line: string, ctx: ParseContext): BattleEvent | null {
  if (!line || !line.startsWith('|')) return null;
  const parts = line.split('|');
  const verb = parts[1] ?? '';

  switch (verb) {
    case 'turn': {
      const turn = parseInt(parts[2] ?? '0', 10);
      return { type: 'turn', turn };
    }
    case 'switch':
    case 'drag': {
      const sideTag = (parts[2] ?? '').slice(0, 2);
      const side = sideTag === 'p1' ? 'p1' : 'p2';
      const pokemonName = (parts[2] ?? '').split(': ')[1] ?? '';
      // parts[3] = "Bulbasaur, L5, M" — species first, comma-separated.
      // Species lowercased to match @pkmn convention + SPECIES_TO_DEX keys.
      const details = parts[3] ?? '';
      const speciesRaw = details.split(',')[0]?.trim() ?? '';
      const species = speciesRaw.toLowerCase();
      return { type: 'switch', side, pokemon: pokemonName, species };
    }
    case 'move': {
      const sideTag = (parts[2] ?? '').slice(0, 2);
      const side = sideTag === 'p1' ? 'p1' : 'p2';
      const pokemonName = (parts[2] ?? '').split(': ')[1] ?? '';
      const moveName = parts[3] ?? '';
      return { type: 'move', side, pokemon: pokemonName, moveName };
    }
    case '-damage': {
      const sideTag = (parts[2] ?? '').slice(0, 2);
      const target = sideTag === 'p1' ? 'p1' : 'p2';
      const hpStr = parts[3] ?? '0/1';
      let hpCurrent = 0;
      let hpMax = 1;
      if (hpStr.includes('fnt')) {
        hpCurrent = 0;
        hpMax = 1;
      } else {
        const [cur, max] = hpStr.split(' ')[0]!.split('/');
        hpCurrent = parseInt(cur ?? '0', 10);
        hpMax = parseInt(max ?? '1', 10);
      }
      return {
        type: 'damage',
        target,
        hpCurrent,
        hpMax,
        hpPercent: (hpCurrent / hpMax) * 100,
      };
    }
    case 'faint': {
      const sideTag = (parts[2] ?? '').slice(0, 2);
      const target = sideTag === 'p1' ? 'p1' : 'p2';
      const pokemonName = (parts[2] ?? '').split(': ')[1] ?? '';
      return { type: 'faint', target, pokemon: pokemonName };
    }
    // Type-effectiveness markers — Showdown emits these on a separate line
    // immediately before the matching '-damage' line for the same target.
    // We pass them through as their own event so BattleScene can latch the
    // effectiveness on the target and consume it on the next damage.
    case '-supereffective':
    case '-resisted':
    case '-immune': {
      const sideTag = (parts[2] ?? '').slice(0, 2);
      const target = sideTag === 'p1' ? 'p1' : 'p2';
      const kind =
        verb === '-supereffective' ? 'super' :
        verb === '-resisted' ? 'resisted' : 'immune';
      return { type: 'effectiveness', target, kind };
    }
    // PR 1 combat text additions.
    case '-crit': {
      const sideTag = (parts[2] ?? '').slice(0, 2);
      const target = sideTag === 'p1' ? 'p1' : 'p2';
      return { type: 'crit', target };
    }
    case '-miss': {
      // |-miss|SOURCE|TARGET — SOURCE is the attacker that missed.
      const sourceTag = (parts[2] ?? '').slice(0, 2);
      const targetTag = (parts[3] ?? '').slice(0, 2);
      const source = sourceTag === 'p1' ? 'p1' : 'p2';
      const target = targetTag === 'p1' ? 'p1' : 'p2';
      return { type: 'miss', source, target };
    }
    case '-fail': {
      const sideTag = (parts[2] ?? '').slice(0, 2);
      const target = sideTag === 'p1' ? 'p1' : 'p2';
      return { type: 'fail', target };
    }
    case '-status': {
      // |-status|POKEMON|STATUS — STATUS is one of psn/par/brn/slp/frz/tox.
      // tox is sim's "badly poisoned" — PR 1 BattleEvent.status only models
      // 5 conditions; tox falls through to null (skipped). PR 3 extends
      // PartyMember.status to include tox + tints its sprite the same
      // purple as psn (cartridge convention).
      const sideTag = (parts[2] ?? '').slice(0, 2);
      const target = sideTag === 'p1' ? 'p1' : 'p2';
      const raw = parts[3] ?? '';
      if (
        raw === 'psn' ||
        raw === 'par' ||
        raw === 'brn' ||
        raw === 'slp' ||
        raw === 'frz'
      ) {
        return { type: 'status', target, status: raw };
      }
      return null;
    }
    case 'cant': {
      // |cant|POKEMON|REASON|[MOVE] — REASON is one of slp/par/frz/flinch
      // (status / paralysis-roll / freeze / flinch) OR a move name like
      // Truant. PR 1 scope = the 4 status reasons; unknown reasons fall
      // through to null (skipped).
      const sideTag = (parts[2] ?? '').slice(0, 2);
      const target = sideTag === 'p1' ? 'p1' : 'p2';
      const raw = parts[3] ?? '';
      if (raw === 'slp' || raw === 'par' || raw === 'frz' || raw === 'flinch') {
        return { type: 'cant', target, reason: raw };
      }
      return null;
    }
    case 'win': {
      const winnerName = parts[2] ?? '';
      const winner = winnerName === ctx.p1Name ? 'p1' : 'p2';
      return { type: 'battleEnd', winner };
    }
    case 'tie': {
      return { type: 'battleEnd', winner: 'draw' };
    }
    default:
      return null;
  }
}
